// Auto-generated Zod schemas for tables in @daymade/db by the generateZodSchemas script.
// We need to do this because otherwise the DB schema appears in the frontend bundle. We can "air-gap" the schema
// from the frontend by generating the output of drizzle-zod to a separate .ts file, rather than referencing the DB
// schema in our api-contracts with calls to drizzle-zod.

import { z } from 'zod';
import {
  AdyenSessionAction,
  AuditAction,
  CmsDocumentStatus,
  DrawOrderStatus,
  DrawScheduleStatus,
  DrawStatus,
  EmailState,
  FeedbackAction,
  FeedbackRefKey,
  GameType,
  GiveawayType,
  LeaderboardAction,
  OrderStatus,
  PaymentCurrency,
  PaymentMethod,
  PaymentRefType,
  PaymentStatus,
  PlayerStatus,
  PrizeDisplayGroup,
  PrizeOrderStatus,
  PrizeStatus,
  PrizeTierType,
  PrizeType,
  PrizeWinStatus,
  ProductStatus,
  ProductType,
  PromoAction,
  PromoPrizeRewardRefKey,
  PromoPrizeRewardStatus,
  PromoRecurringStatus,
  PromoStatus,
  PromoType,
  PurchasingAction,
  ReferralScheme,
  ReferralStatus,
  RewardType,
  ScheduledTaskStatus,
  SubscriptionRunStatus,
  SubscriptionStatus,
  TaskQueueType,
  TaskStatus,
  TicketStatus,
  TokenOrderStatus,
  TokenRedemptionOptionGroup,
  TokenRedemptionOptionStatus,
  TokenRedemptionOptionType,
  TokenRedemptionStatus,
  TransactionType,
  TreeAction,
  TreeRefKey,
  TreeStatus,
  VendorCategory,
  WinType,
} from '@daymade/common-lib/dbEnums';

import {
  CmsTypeSlugToZDocumentData,
  ReviewConfig,
  WinnerConfig,
  WinnerPolaroidConfig,
  ZBigRaffleEntry,
  ZBigRaffleResult,
  ZCmsDocumentData,
  ZDrawTypeFullResult,
  ZDrawTypeNumberSetup,
  ZEmailBrevoData,
  ZEntryLine,
  ZFeedbackContent,
  ZLeaderboardEntry,
  ZLeaderboardResult,
  ZLeaderboardResultAdditionalInfo,
  ZLotteryDrawEntry,
  ZLotteryDrawResult,
  ZOrderAttributionParams,
  ZOrderUtmParams,
  ZPlayerABTestVariant,
  ZPriceTable,
  ZPrizeImageLocations,
  ZPrizeImageLocationsBase,
  ZPromoConditions,
  ZRaffleEntry,
  ZRaffleResult,
  ZReviewDocumentData,
  ZSimulatedWinners,
  ZSubscriptionHistoryAdditionalInfo,
  ZWinnerDocumentData,
  ZWinnerPolaroidDocumentData,
  TDrawTypeNumberSetup,
  TFeedbackContent,
} from '@daymade/common-lib/dbJsonModels';
/** The Zod schema for a SELECT of the Account table */
export const zodSelectAccount = z.object({
  playerId: z.string(), // Autogenerated
  currencyCode: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  mainBalance: z.number(), // Autogenerated
  giftCardBalance: z.number(), // Autogenerated
  promoBalance: z.number(), // Autogenerated
  tokenBalance: z.number(), // Autogenerated
});
/** The Zod schema for an INSERT of the Account table */
export const zodInsertAccount = z.object({
  playerId: z.string(), // Autogenerated
  currencyCode: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  mainBalance: z.number().optional(), // Autogenerated
  giftCardBalance: z.number().optional(), // Autogenerated
  promoBalance: z.number().optional(), // Autogenerated
  tokenBalance: z.number().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the AccountJournal table */
export const zodSelectAccountJournal = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  playerId: z.string(), // Autogenerated
  totalFiatAmount: z.number(), // Autogenerated
  balanceAmount: z.number(), // Autogenerated
  giftCardAmount: z.number(), // Autogenerated
  promoAmount: z.number(), // Autogenerated
  paymentMethodAmount: z.number(), // Autogenerated
  tokenAmount: z.number(), // Autogenerated
  transactionType: z.nativeEnum(TransactionType), // Autogenerated
  comment: z.string().nullable(), // Autogenerated
  refType: z.nativeEnum(PaymentRefType), // Autogenerated
  refId: z.string(), // Autogenerated
  paymentMethod: z.nativeEnum(PaymentMethod).nullable(), // Autogenerated
  paymentSessionId: z.string().nullable(), // Autogenerated
  internal: z.boolean(), // Autogenerated
  migrationRef: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the AccountJournal table */
export const zodInsertAccountJournal = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  totalFiatAmount: z.number(), // Autogenerated
  balanceAmount: z.number(), // Autogenerated
  giftCardAmount: z.number(), // Autogenerated
  promoAmount: z.number(), // Autogenerated
  paymentMethodAmount: z.number(), // Autogenerated
  tokenAmount: z.number(), // Autogenerated
  transactionType: z.nativeEnum(TransactionType), // Autogenerated
  comment: z.string().nullable().optional(), // Autogenerated
  refType: z.nativeEnum(PaymentRefType), // Autogenerated
  refId: z.string(), // Autogenerated
  paymentMethod: z.nativeEnum(PaymentMethod).nullable().optional(), // Autogenerated
  paymentSessionId: z.string().nullable().optional(), // Autogenerated
  internal: z.boolean().optional(), // Autogenerated
  migrationRef: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the ActivityFeedItem table */
export const zodSelectActivityFeedItem = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  lastModified: z.coerce.date(), // Autogenerated
  actionCode: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  refId: z.string().nullable(), // Autogenerated
  refKey: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['prize_win', 'feedback', 'token_order', 'token_redemption'],
    )
    .nullable(), // Autogenerated
  displayString: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['ActivityFeedItem','placeholderData'] if the type is needed */
  placeholderData: z.unknown().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the ActivityFeedItem table */
export const zodInsertActivityFeedItem = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  lastModified: z.coerce.date().optional(), // Autogenerated
  actionCode: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  refId: z.string().nullable().optional(), // Autogenerated
  refKey: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['prize_win', 'feedback', 'token_order', 'token_redemption'],
    )
    .nullable()
    .optional(), // Autogenerated
  displayString: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['ActivityFeedItem','placeholderData'] if the type is needed */
  placeholderData: z.unknown().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the CmsDocument table */
export const zodSelectCmsDocument = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  typeSlug: z.string(), // Autogenerated
  data: ZCmsDocumentData, // Autogenerated
  status: z.nativeEnum(CmsDocumentStatus), // Autogenerated
  sortOrder: z.number(), // Autogenerated
});
/** The Zod schema for an INSERT of the CmsDocument table */
export const zodInsertCmsDocument = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  typeSlug: z.string(), // Autogenerated
  data: ZCmsDocumentData.optional(), // Autogenerated
  status: z.nativeEnum(CmsDocumentStatus).optional(), // Autogenerated
  sortOrder: z.number().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the Draw table */
export const zodSelectDraw = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  drawScheduleId: z.number(), // Autogenerated
  drawDate: z.coerce.date().nullable(), // Autogenerated
  status: z.nativeEnum(DrawStatus), // Autogenerated
  name: z.string(), // Autogenerated
  numEntries: z.number(), // Autogenerated
  description: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Draw','pendingResult'] if the type is needed */
  pendingResult: z.unknown().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Draw','result'] if the type is needed */
  result: z.unknown().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Draw','simulatedWinners'] if the type is needed */
  simulatedWinners: z.unknown().nullable(), // Autogenerated
  verified: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['SENT', 'VERIFIED'],
    )
    .nullable(), // Autogenerated
  prizesAwardedAt: z.coerce.date().nullable(), // Autogenerated
  winnersNotified: z.boolean().nullable(), // Autogenerated
  participantsNotified: z.boolean().nullable(), // Autogenerated
  prizeId: z.number().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the Draw table */
export const zodInsertDraw = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  drawScheduleId: z.number(), // Autogenerated
  drawDate: z.coerce.date().nullable().optional(), // Autogenerated
  status: z.nativeEnum(DrawStatus).optional(), // Autogenerated
  name: z.string(), // Autogenerated
  numEntries: z.number().optional(), // Autogenerated
  description: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Draw','pendingResult'] if the type is needed */
  pendingResult: z.unknown().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Draw','result'] if the type is needed */
  result: z.unknown().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Draw','simulatedWinners'] if the type is needed */
  simulatedWinners: z.unknown().nullable().optional(), // Autogenerated
  verified: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['SENT', 'VERIFIED'],
    )
    .nullable()
    .optional(), // Autogenerated
  prizesAwardedAt: z.coerce.date().nullable().optional(), // Autogenerated
  winnersNotified: z.boolean().nullable().optional(), // Autogenerated
  participantsNotified: z.boolean().nullable().optional(), // Autogenerated
  prizeId: z.number().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the DrawOrder table */
export const zodSelectDrawOrder = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  orderId: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  status: z.nativeEnum(DrawOrderStatus), // Autogenerated
  price: z.number(), // Autogenerated
  firstDrawId: z.number().nullable(), // Autogenerated
  drawTypeCode: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['DrawOrder','entries'] if the type is needed */
  entries: z.unknown(), // Autogenerated
});
/** The Zod schema for an INSERT of the DrawOrder table */
export const zodInsertDrawOrder = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  orderId: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  status: z.nativeEnum(DrawOrderStatus).optional(), // Autogenerated
  price: z.number(), // Autogenerated
  firstDrawId: z.number().nullable().optional(), // Autogenerated
  drawTypeCode: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['DrawOrder','entries'] if the type is needed */
  entries: z.unknown(), // Autogenerated
});

/** The Zod schema for a SELECT of the DrawOrderSchedulePlan table */
export const zodSelectDrawOrderSchedulePlan = z.object({
  drawOrderId: z.string(), // Autogenerated
  drawScheduleId: z.number(), // Autogenerated
  drawTypePlanId: z.number(), // Autogenerated
});
/** The Zod schema for an INSERT of the DrawOrderSchedulePlan table */
export const zodInsertDrawOrderSchedulePlan = z.object({
  drawOrderId: z.string(), // Autogenerated
  drawScheduleId: z.number(), // Autogenerated
  drawTypePlanId: z.number(), // Autogenerated
});

/** The Zod schema for a SELECT of the DrawSchedule table */
export const zodSelectDrawSchedule = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  templateName: z.string(), // Autogenerated
  description: z.string().nullable(), // Autogenerated
  code: z.string(), // Autogenerated
  status: z.nativeEnum(DrawScheduleStatus), // Autogenerated
  drawTypeCode: z.string(), // Autogenerated
  schedule: z.string().nullable(), // Autogenerated
  autoRun: z.boolean().nullable(), // Autogenerated
  verificationScheme: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['INSURED'],
    )
    .nullable(), // Autogenerated
  futureDrawCount: z.number(), // Autogenerated
  prizeStructureId: z.number(), // Autogenerated
  rotatePrize: z.boolean(), // Autogenerated
  allowManualDrawCreation: z.boolean(), // Autogenerated
});
/** The Zod schema for an INSERT of the DrawSchedule table */
export const zodInsertDrawSchedule = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  templateName: z.string(), // Autogenerated
  description: z.string().nullable().optional(), // Autogenerated
  code: z.string(), // Autogenerated
  status: z.nativeEnum(DrawScheduleStatus).optional(), // Autogenerated
  drawTypeCode: z.string(), // Autogenerated
  schedule: z.string().nullable().optional(), // Autogenerated
  autoRun: z.boolean().nullable().optional(), // Autogenerated
  verificationScheme: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['INSURED'],
    )
    .nullable()
    .optional(), // Autogenerated
  futureDrawCount: z.number(), // Autogenerated
  prizeStructureId: z.number(), // Autogenerated
  rotatePrize: z.boolean().optional(), // Autogenerated
  allowManualDrawCreation: z.boolean().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the DrawType table */
export const zodSelectDrawType = z.object({
  code: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  name: z.string(), // Autogenerated
  gameType: z.nativeEnum(GameType), // Autogenerated
  numberSetup: ZDrawTypeNumberSetup.nullable(), // Autogenerated
  entryCap: z.number().nullable(), // Autogenerated
  maxPlayerEntries: z.number().nullable(), // Autogenerated
  group: z.string().nullable(), // Autogenerated
  dispOrder: z.number(), // Autogenerated
});
/** The Zod schema for an INSERT of the DrawType table */
export const zodInsertDrawType = z.object({
  code: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  name: z.string(), // Autogenerated
  gameType: z.nativeEnum(GameType), // Autogenerated
  numberSetup: ZDrawTypeNumberSetup.nullable().optional(), // Autogenerated
  entryCap: z.number().nullable().optional(), // Autogenerated
  maxPlayerEntries: z.number().nullable().optional(), // Autogenerated
  group: z.string().nullable().optional(), // Autogenerated
  dispOrder: z.number().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the DrawTypePlan table */
export const zodSelectDrawTypePlan = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  drawTypeCode: z.string(), // Autogenerated
  code: z.string(), // Autogenerated
  subcode: z.string(), // Autogenerated
  name: z.string(), // Autogenerated
  desc: z.string().nullable(), // Autogenerated
  priceVersion: z.number(), // Autogenerated
  priceTable: ZPriceTable, // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['DrawTypePlan','subscriptionMaxEntries'] if the type is needed */
  subscriptionMaxEntries: z.unknown(), // Autogenerated
});
/** The Zod schema for an INSERT of the DrawTypePlan table */
export const zodInsertDrawTypePlan = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  drawTypeCode: z.string(), // Autogenerated
  code: z.string(), // Autogenerated
  subcode: z.string(), // Autogenerated
  name: z.string(), // Autogenerated
  desc: z.string().nullable().optional(), // Autogenerated
  priceVersion: z.number().optional(), // Autogenerated
  priceTable: ZPriceTable, // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['DrawTypePlan','subscriptionMaxEntries'] if the type is needed */
  subscriptionMaxEntries: z.unknown().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the DrawTypePlanPrizeTier table */
export const zodSelectDrawTypePlanPrizeTier = z.object({
  drawTypePlanId: z.number(), // Autogenerated
  drawScheduleId: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  numberMatches: z.number(), // Autogenerated
  bonusNumberMatches: z.number(), // Autogenerated
  prizeTierId: z.number(), // Autogenerated
});
/** The Zod schema for an INSERT of the DrawTypePlanPrizeTier table */
export const zodInsertDrawTypePlanPrizeTier = z.object({
  drawTypePlanId: z.number(), // Autogenerated
  drawScheduleId: z.number(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  numberMatches: z.number(), // Autogenerated
  bonusNumberMatches: z.number(), // Autogenerated
  prizeTierId: z.number(), // Autogenerated
});

/** The Zod schema for a SELECT of the Email table */
export const zodSelectEmail = z.object({
  id: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  lastModified: z.coerce.date(), // Autogenerated
  templateName: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Email','templateData'] if the type is needed */
  templateData: z.unknown().nullable(), // Autogenerated
  state: z.string().nullable(), // Autogenerated
  unsubscribed: z.boolean().nullable(), // Autogenerated
  brevoMessageId: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Email','brevoStatuses'] if the type is needed */
  brevoStatuses: z.unknown().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the Email table */
export const zodInsertEmail = z.object({
  id: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  lastModified: z.coerce.date().optional(), // Autogenerated
  templateName: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Email','templateData'] if the type is needed */
  templateData: z.unknown().nullable().optional(), // Autogenerated
  state: z.string().nullable().optional(), // Autogenerated
  unsubscribed: z.boolean().nullable().optional(), // Autogenerated
  brevoMessageId: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Email','brevoStatuses'] if the type is needed */
  brevoStatuses: z.unknown().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the Feedback table */
export const zodSelectFeedback = z.object({
  id: z.string(), // Autogenerated
  playerId: z.string().nullable(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  action: z.nativeEnum(FeedbackAction), // Autogenerated
  feedback: ZFeedbackContent, // Autogenerated
  refKey: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['prize_win', 'subscription', 'no_key'],
    )
    .nullable(), // Autogenerated
  refId: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the Feedback table */
export const zodInsertFeedback = z.object({
  id: z.string().optional(), // Autogenerated
  playerId: z.string().nullable().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  action: z.nativeEnum(FeedbackAction), // Autogenerated
  feedback: ZFeedbackContent, // Autogenerated
  refKey: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['prize_win', 'subscription', 'no_key'],
    )
    .nullable()
    .optional(), // Autogenerated
  refId: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the IntegratedGiftCard table */
export const zodSelectIntegratedGiftCard = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  provider: z.string(), // Autogenerated
  redeemCode: z.string().nullable(), // Autogenerated
  redeemUrl: z.string().nullable(), // Autogenerated
  expiresAt: z.coerce.date().nullable(), // Autogenerated
  denomination: z.number().nullable(), // Autogenerated
  code: z.string().nullable(), // Autogenerated
  playerId: z.string(), // Autogenerated
  prizeOrderProductId: z.string().nullable(), // Autogenerated
  giveawayId: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['IntegratedGiftCard','metadata'] if the type is needed */
  metadata: z.unknown(), // Autogenerated
});
/** The Zod schema for an INSERT of the IntegratedGiftCard table */
export const zodInsertIntegratedGiftCard = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  provider: z.string(), // Autogenerated
  redeemCode: z.string().nullable().optional(), // Autogenerated
  redeemUrl: z.string().nullable().optional(), // Autogenerated
  expiresAt: z.coerce.date().nullable().optional(), // Autogenerated
  denomination: z.number().nullable().optional(), // Autogenerated
  code: z.string().nullable().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  prizeOrderProductId: z.string().nullable().optional(), // Autogenerated
  giveawayId: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['IntegratedGiftCard','metadata'] if the type is needed */
  metadata: z.unknown().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the InvitedBy table */
export const zodSelectInvitedBy = z.object({
  inviteeId: z.string(), // Autogenerated
  inviterId: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  lastModified: z.coerce.date(), // Autogenerated
  bonusScheme: z.nativeEnum(ReferralScheme), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['InvitedBy','inviteeProgress'] if the type is needed */
  inviteeProgress: z.unknown().nullable(), // Autogenerated
  inviteeBonusStatus: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['PENDING', 'INVALIDATED', 'INPROGRESS', 'CLAIMED'],
    )
    .nullable(), // Autogenerated
  inviterBonusStatus: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['PENDING', 'PENDING_REVIEW', 'INVALIDATED', 'CLAIMED'],
    )
    .nullable(), // Autogenerated
  status: z.nativeEnum(ReferralStatus).nullable(), // Autogenerated
  inviterGracePeriodExpiresAt: z.coerce.date().nullable(), // Autogenerated
  inviteeGracePeriodExpiresAt: z.coerce.date().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the InvitedBy table */
export const zodInsertInvitedBy = z.object({
  inviteeId: z.string(), // Autogenerated
  inviterId: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  lastModified: z.coerce.date().optional(), // Autogenerated
  bonusScheme: z.nativeEnum(ReferralScheme), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['InvitedBy','inviteeProgress'] if the type is needed */
  inviteeProgress: z.unknown().nullable().optional(), // Autogenerated
  inviteeBonusStatus: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['PENDING', 'INVALIDATED', 'INPROGRESS', 'CLAIMED'],
    )
    .nullable()
    .optional(), // Autogenerated
  inviterBonusStatus: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['PENDING', 'PENDING_REVIEW', 'INVALIDATED', 'CLAIMED'],
    )
    .nullable()
    .optional(), // Autogenerated
  status: z.nativeEnum(ReferralStatus).nullable().optional(), // Autogenerated
  inviterGracePeriodExpiresAt: z.coerce.date().nullable().optional(), // Autogenerated
  inviteeGracePeriodExpiresAt: z.coerce.date().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the Order table */
export const zodSelectOrder = z.object({
  id: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  status: z.nativeEnum(OrderStatus), // Autogenerated
  isSubscription: z.boolean(), // Autogenerated
  totalFiatAmount: z.number(), // Autogenerated
  tokenAmount: z.number(), // Autogenerated
  paymentMethod: z.nativeEnum(PaymentMethod).nullable(), // Autogenerated
  subscriptionId: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Order','utmParams'] if the type is needed */
  utmParams: z.unknown().nullable(), // Autogenerated
  attributionParams: ZOrderAttributionParams.nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the Order table */
export const zodInsertOrder = z.object({
  id: z.string().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  status: z.nativeEnum(OrderStatus).optional(), // Autogenerated
  isSubscription: z.boolean().optional(), // Autogenerated
  totalFiatAmount: z.number(), // Autogenerated
  tokenAmount: z.number(), // Autogenerated
  paymentMethod: z.nativeEnum(PaymentMethod).nullable().optional(), // Autogenerated
  subscriptionId: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Order','utmParams'] if the type is needed */
  utmParams: z.unknown().nullable().optional(), // Autogenerated
  attributionParams: ZOrderAttributionParams.nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the PaymentRequest table */
export const zodSelectPaymentRequest = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  playerId: z.string(), // Autogenerated
  ticketIds: z.array(z.string()).nullable(), // Autogenerated
  status: z.nativeEnum(PaymentStatus), // Autogenerated
  purchasingAction: z.nativeEnum(PurchasingAction), // Autogenerated
  totalFiatAmount: z.number(), // Autogenerated
  balanceAmount: z.number(), // Autogenerated
  giftCardAmount: z.number(), // Autogenerated
  promoAmount: z.number(), // Autogenerated
  tokenAmount: z.number(), // Autogenerated
  discountAmount: z.number(), // Autogenerated
  paymentMethodAmount: z.number(), // Autogenerated
  refType: z.nativeEnum(PaymentRefType), // Autogenerated
  refId: z.string(), // Autogenerated
  paymentMethod: z.nativeEnum(PaymentMethod).nullable(), // Autogenerated
  adyenPaymentSessionId: z.string().nullable(), // Autogenerated
  promoCode: z.string().nullable(), // Autogenerated
  referralId: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the PaymentRequest table */
export const zodInsertPaymentRequest = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  ticketIds: z.array(z.string()).nullable().optional(), // Autogenerated
  status: z.nativeEnum(PaymentStatus).optional(), // Autogenerated
  purchasingAction: z.nativeEnum(PurchasingAction), // Autogenerated
  totalFiatAmount: z.number().optional(), // Autogenerated
  balanceAmount: z.number().optional(), // Autogenerated
  giftCardAmount: z.number().optional(), // Autogenerated
  promoAmount: z.number().optional(), // Autogenerated
  tokenAmount: z.number().optional(), // Autogenerated
  discountAmount: z.number().optional(), // Autogenerated
  paymentMethodAmount: z.number().optional(), // Autogenerated
  refType: z.nativeEnum(PaymentRefType), // Autogenerated
  refId: z.string(), // Autogenerated
  paymentMethod: z.nativeEnum(PaymentMethod).nullable().optional(), // Autogenerated
  adyenPaymentSessionId: z.string().nullable().optional(), // Autogenerated
  promoCode: z.string().nullable().optional(), // Autogenerated
  referralId: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the Player table */
export const zodSelectPlayer = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  emailAddress: z.string(), // Autogenerated
  firstName: z.string(), // Autogenerated
  lastName: z.string(), // Autogenerated
  phoneNumber: z.string().nullable(), // Autogenerated
  referralId: z.string(), // Autogenerated
  referralLinkCopyCount: z.number().nullable(), // Autogenerated
  status: z.nativeEnum(PlayerStatus), // Autogenerated
  role: z.enum(
    /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
    ['PLAYER', 'ADMIN', 'OPERATOR', 'STATS'],
  ), // Autogenerated
  isWinnerManager: z.boolean().nullable(), // Autogenerated
  gender: z.string().nullable(), // Autogenerated
  dateOfBirth: z.coerce.date().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Player','interests'] if the type is needed */
  interests: z.unknown().nullable(), // Autogenerated
  initials: z.string().nullable(), // Autogenerated
  treesPlanted: z.number(), // Autogenerated
  passwordHash: z.string().nullable(), // Autogenerated
  facebookUserId: z.string().nullable(), // Autogenerated
  googleUserId: z.string().nullable(), // Autogenerated
  appleUserId: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Player','utmParameters'] if the type is needed */
  utmParameters: z.unknown().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Player','metadata'] if the type is needed */
  metadata: z.unknown().nullable(), // Autogenerated
  abtestVariants: ZPlayerABTestVariant.nullable(), // Autogenerated
  avatarLocation: z.string().nullable(), // Autogenerated
  friendlyName: z.string().nullable(), // Autogenerated
  searchFullName: z.string().nullable(), // Autogenerated
  searchEmail: z.string().nullable(), // Autogenerated
  reviewedOnTrustpilot: z.boolean(), // Autogenerated
  reviewedOnFacebook: z.boolean(), // Autogenerated
  reviewedOnGoogle: z.boolean(), // Autogenerated
  followedOnInstagram: z.boolean(), // Autogenerated
  filledInSurvey: z.boolean(), // Autogenerated
  firstPurchaseTicketId: z.string().nullable(), // Autogenerated
  lastPurchaseTicketId: z.string().nullable(), // Autogenerated
  totalSpend: z.number().nullable(), // Autogenerated
  suspended: z.boolean().nullable(), // Autogenerated
  suspendedUntil: z.coerce.date().nullable(), // Autogenerated
  frozen: z.boolean(), // Autogenerated
  spendLimitPence: z.number().nullable(), // Autogenerated
  lastActivityAt: z.coerce.date().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the Player table */
export const zodInsertPlayer = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  emailAddress: z.string(), // Autogenerated
  firstName: z.string(), // Autogenerated
  lastName: z.string(), // Autogenerated
  phoneNumber: z.string().nullable().optional(), // Autogenerated
  referralId: z.string(), // Autogenerated
  referralLinkCopyCount: z.number().nullable().optional(), // Autogenerated
  status: z.nativeEnum(PlayerStatus).optional(), // Autogenerated
  role: z
    .enum(
      /* If you want this enum to be strongly-typed (instead of a string-union), please add it to DbEnums, and run the generateZodSchemas script again */
      ['PLAYER', 'ADMIN', 'OPERATOR', 'STATS'],
    )
    .optional(), // Autogenerated
  isWinnerManager: z.boolean().nullable().optional(), // Autogenerated
  gender: z.string().nullable().optional(), // Autogenerated
  dateOfBirth: z.coerce.date().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Player','interests'] if the type is needed */
  interests: z.unknown().nullable().optional(), // Autogenerated
  initials: z.string().nullable().optional(), // Autogenerated
  treesPlanted: z.number().optional(), // Autogenerated
  passwordHash: z.string().nullable().optional(), // Autogenerated
  facebookUserId: z.string().nullable().optional(), // Autogenerated
  googleUserId: z.string().nullable().optional(), // Autogenerated
  appleUserId: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Player','utmParameters'] if the type is needed */
  utmParameters: z.unknown().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Player','metadata'] if the type is needed */
  metadata: z.unknown().nullable().optional(), // Autogenerated
  abtestVariants: ZPlayerABTestVariant.nullable().optional(), // Autogenerated
  avatarLocation: z.string().nullable().optional(), // Autogenerated
  friendlyName: z.string().nullable().optional(), // Autogenerated
  searchFullName: z.string().nullable().optional(), // Autogenerated
  searchEmail: z.string().nullable().optional(), // Autogenerated
  reviewedOnTrustpilot: z.boolean().optional(), // Autogenerated
  reviewedOnFacebook: z.boolean().optional(), // Autogenerated
  reviewedOnGoogle: z.boolean().optional(), // Autogenerated
  followedOnInstagram: z.boolean().optional(), // Autogenerated
  filledInSurvey: z.boolean().optional(), // Autogenerated
  firstPurchaseTicketId: z.string().nullable().optional(), // Autogenerated
  lastPurchaseTicketId: z.string().nullable().optional(), // Autogenerated
  totalSpend: z.number().nullable().optional(), // Autogenerated
  suspended: z.boolean().nullable().optional(), // Autogenerated
  suspendedUntil: z.coerce.date().nullable().optional(), // Autogenerated
  frozen: z.boolean().optional(), // Autogenerated
  spendLimitPence: z.number().nullable().optional(), // Autogenerated
  lastActivityAt: z.coerce.date().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the PlayerIPAddressLog table */
export const zodSelectPlayerIPAddressLog = z.object({
  id: z.number(), // Autogenerated
  ipAddress: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  lastSeenAt: z.coerce.date(), // Autogenerated
});
/** The Zod schema for an INSERT of the PlayerIPAddressLog table */
export const zodInsertPlayerIPAddressLog = z.object({
  id: z.number().optional(), // Autogenerated
  ipAddress: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  lastSeenAt: z.coerce.date().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the PlayerPreferences table */
export const zodSelectPlayerPreferences = z.object({
  playerId: z.string(), // Autogenerated
  agreedToMarketing: z.boolean(), // Autogenerated
  drawReminderEmails: z.boolean().nullable(), // Autogenerated
  dprReminders: z.boolean().nullable(), // Autogenerated
  notifyResults: z.boolean(), // Autogenerated
  notifyResultsDailyChallenger: z.boolean().nullable(), // Autogenerated
  notifyResultsLeaderboard: z.boolean().nullable(), // Autogenerated
  notifyResultsRaffles: z.boolean().nullable(), // Autogenerated
  notifyResultsMinilottos: z.boolean().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the PlayerPreferences table */
export const zodInsertPlayerPreferences = z.object({
  playerId: z.string(), // Autogenerated
  agreedToMarketing: z.boolean().optional(), // Autogenerated
  drawReminderEmails: z.boolean().nullable().optional(), // Autogenerated
  dprReminders: z.boolean().nullable().optional(), // Autogenerated
  notifyResults: z.boolean().optional(), // Autogenerated
  notifyResultsDailyChallenger: z.boolean().nullable().optional(), // Autogenerated
  notifyResultsLeaderboard: z.boolean().nullable().optional(), // Autogenerated
  notifyResultsRaffles: z.boolean().nullable().optional(), // Autogenerated
  notifyResultsMinilottos: z.boolean().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the Prize table */
export const zodSelectPrize = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  status: z.nativeEnum(PrizeStatus), // Autogenerated
  title: z.string(), // Autogenerated
  subheading: z.string(), // Autogenerated
  badge: z.string().nullable(), // Autogenerated
  type: z.nativeEnum(PrizeType), // Autogenerated
  slug: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Prize','imageLocations'] if the type is needed */
  imageLocations: z.unknown(), // Autogenerated
  overviewBody: z.string().nullable(), // Autogenerated
  whatsIncludedHeading: z.string().nullable(), // Autogenerated
  whatsIncludedBody: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Prize','internalInfo'] if the type is needed */
  internalInfo: z.unknown(), // Autogenerated
  requiresDeliveryAddress: z.boolean().nullable(), // Autogenerated
  requiresEmailAddress: z.boolean().nullable(), // Autogenerated
  requiresPhoneNumber: z.boolean().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Prize','additionalQuestionsForPlayer'] if the type is needed */
  additionalQuestionsForPlayer: z.unknown(), // Autogenerated
  seoMetaTitle: z.string().nullable(), // Autogenerated
  seoMetaDescription: z.string().nullable(), // Autogenerated
  seoKeywords: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Prize','seoImageAltText'] if the type is needed */
  seoImageAltText: z.unknown(), // Autogenerated
  isFromPlayersSuggestion: z.boolean().nullable(), // Autogenerated
  followUpEmailDelayInDays: z.number().nullable(), // Autogenerated
  subscriberDiscountContentCode: z.string().nullable(), // Autogenerated
  vendorCategory: z.nativeEnum(VendorCategory).nullable(), // Autogenerated
  vendorName: z.string().nullable(), // Autogenerated
  legacyPrizeValue: z.number().nullable(), // Autogenerated
  searchVector: z.any(), // Autogenerated
  sortOrder: z.number(), // Autogenerated
  mainCategoryCode: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the Prize table */
export const zodInsertPrize = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  status: z.nativeEnum(PrizeStatus), // Autogenerated
  title: z.string(), // Autogenerated
  subheading: z.string(), // Autogenerated
  badge: z.string().nullable().optional(), // Autogenerated
  type: z.nativeEnum(PrizeType), // Autogenerated
  slug: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Prize','imageLocations'] if the type is needed */
  imageLocations: z.unknown(), // Autogenerated
  overviewBody: z.string().nullable().optional(), // Autogenerated
  whatsIncludedHeading: z.string().nullable().optional(), // Autogenerated
  whatsIncludedBody: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Prize','internalInfo'] if the type is needed */
  internalInfo: z.unknown().optional(), // Autogenerated
  requiresDeliveryAddress: z.boolean().nullable().optional(), // Autogenerated
  requiresEmailAddress: z.boolean().nullable().optional(), // Autogenerated
  requiresPhoneNumber: z.boolean().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Prize','additionalQuestionsForPlayer'] if the type is needed */
  additionalQuestionsForPlayer: z.unknown().optional(), // Autogenerated
  seoMetaTitle: z.string().nullable().optional(), // Autogenerated
  seoMetaDescription: z.string().nullable().optional(), // Autogenerated
  seoKeywords: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['Prize','seoImageAltText'] if the type is needed */
  seoImageAltText: z.unknown(), // Autogenerated
  isFromPlayersSuggestion: z.boolean().nullable().optional(), // Autogenerated
  followUpEmailDelayInDays: z.number().nullable().optional(), // Autogenerated
  subscriberDiscountContentCode: z.string().nullable().optional(), // Autogenerated
  vendorCategory: z.nativeEnum(VendorCategory).nullable().optional(), // Autogenerated
  vendorName: z.string().nullable().optional(), // Autogenerated
  legacyPrizeValue: z.number().nullable().optional(), // Autogenerated
  searchVector: z.any(), // Autogenerated
  sortOrder: z.number().optional(), // Autogenerated
  mainCategoryCode: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the PrizeTier table */
export const zodSelectPrizeTier = z.object({
  id: z.number(), // Autogenerated
  prizeStructureId: z.number(), // Autogenerated
  code: z.string(), // Autogenerated
  legacyPrizeValue: z.number().nullable(), // Autogenerated
  imageLink: z.string().nullable(), // Autogenerated
  type: z.nativeEnum(PrizeTierType), // Autogenerated
});
/** The Zod schema for an INSERT of the PrizeTier table */
export const zodInsertPrizeTier = z.object({
  id: z.number().optional(), // Autogenerated
  prizeStructureId: z.number(), // Autogenerated
  code: z.string(), // Autogenerated
  legacyPrizeValue: z.number().nullable().optional(), // Autogenerated
  imageLink: z.string().nullable().optional(), // Autogenerated
  type: z.nativeEnum(PrizeTierType), // Autogenerated
});

/** The Zod schema for a SELECT of the ProductCatalog table */
export const zodSelectProductCatalog = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  name: z.string(), // Autogenerated
  description: z.string().nullable(), // Autogenerated
  status: z.nativeEnum(ProductStatus), // Autogenerated
  type: z.nativeEnum(ProductType), // Autogenerated
  autoFulfilmentCode: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['ProductCatalog','autoFulfilmentDetail'] if the type is needed */
  autoFulfilmentDetail: z.unknown(), // Autogenerated
  imageLocation: z.string().nullable(), // Autogenerated
  price: z.number().nullable(), // Autogenerated
  internalName: z.string().nullable(), // Autogenerated
  productCode: z.string().nullable(), // Autogenerated
  discountCode: z.string().nullable(), // Autogenerated
  purchaseFrom: z.string().nullable(), // Autogenerated
  orderForm: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the ProductCatalog table */
export const zodInsertProductCatalog = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  name: z.string(), // Autogenerated
  description: z.string().nullable().optional(), // Autogenerated
  status: z.nativeEnum(ProductStatus).optional(), // Autogenerated
  type: z.nativeEnum(ProductType), // Autogenerated
  autoFulfilmentCode: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['ProductCatalog','autoFulfilmentDetail'] if the type is needed */
  autoFulfilmentDetail: z.unknown().optional(), // Autogenerated
  imageLocation: z.string().nullable().optional(), // Autogenerated
  price: z.number().nullable().optional(), // Autogenerated
  internalName: z.string().nullable().optional(), // Autogenerated
  productCode: z.string().nullable().optional(), // Autogenerated
  discountCode: z.string().nullable().optional(), // Autogenerated
  purchaseFrom: z.string().nullable().optional(), // Autogenerated
  orderForm: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the PromoCode table */
export const zodSelectPromoCode = z.object({
  code: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  expiresAt: z.coerce.date().nullable(), // Autogenerated
  status: z.nativeEnum(PromoStatus), // Autogenerated
  description: z.string(), // Autogenerated
  banner: z.string().nullable(), // Autogenerated
  promoType: z.nativeEnum(PromoType), // Autogenerated
  allowMulti: z.boolean(), // Autogenerated
  playerSpecific: z.boolean(), // Autogenerated
  action: z.nativeEnum(PromoAction).nullable(), // Autogenerated
  conditions: ZPromoConditions.nullable(), // Autogenerated
  rewardType: z.nativeEnum(RewardType), // Autogenerated
  rewardValue: z.number(), // Autogenerated
  rewardCount: z.number(), // Autogenerated
});
/** The Zod schema for an INSERT of the PromoCode table */
export const zodInsertPromoCode = z.object({
  code: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  expiresAt: z.coerce.date().nullable().optional(), // Autogenerated
  status: z.nativeEnum(PromoStatus).optional(), // Autogenerated
  description: z.string(), // Autogenerated
  banner: z.string().nullable().optional(), // Autogenerated
  promoType: z.nativeEnum(PromoType), // Autogenerated
  allowMulti: z.boolean().optional(), // Autogenerated
  playerSpecific: z.boolean().optional(), // Autogenerated
  action: z.nativeEnum(PromoAction).nullable().optional(), // Autogenerated
  conditions: ZPromoConditions.nullable().optional(), // Autogenerated
  rewardType: z.nativeEnum(RewardType), // Autogenerated
  rewardValue: z.number(), // Autogenerated
  rewardCount: z.number(), // Autogenerated
});

/** The Zod schema for a SELECT of the PromoCodeRecurring table */
export const zodSelectPromoCodeRecurring = z.object({
  id: z.string(), // Autogenerated
  status: z.nativeEnum(PromoRecurringStatus), // Autogenerated
  code: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  playerId: z.string(), // Autogenerated
  subscriptionId: z.string().nullable(), // Autogenerated
  remainingCount: z.number(), // Autogenerated
});
/** The Zod schema for an INSERT of the PromoCodeRecurring table */
export const zodInsertPromoCodeRecurring = z.object({
  id: z.string().optional(), // Autogenerated
  status: z.nativeEnum(PromoRecurringStatus).optional(), // Autogenerated
  code: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  subscriptionId: z.string().nullable().optional(), // Autogenerated
  remainingCount: z.number(), // Autogenerated
});

/** The Zod schema for a SELECT of the PromoPrizeReward table */
export const zodSelectPromoPrizeReward = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  code: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  prizeId: z.number().nullable(), // Autogenerated
  prizeTierId: z.number().nullable(), // Autogenerated
  expiresAt: z.coerce.date().nullable(), // Autogenerated
  chosenAt: z.coerce.date().nullable(), // Autogenerated
  status: z.nativeEnum(PromoPrizeRewardStatus), // Autogenerated
  refKey: z.nativeEnum(PromoPrizeRewardRefKey).nullable(), // Autogenerated
  refId: z.number().nullable(), // Autogenerated
  refIdUuid: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the PromoPrizeReward table */
export const zodInsertPromoPrizeReward = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  code: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  prizeId: z.number().nullable().optional(), // Autogenerated
  prizeTierId: z.number().nullable().optional(), // Autogenerated
  expiresAt: z.coerce.date().nullable().optional(), // Autogenerated
  chosenAt: z.coerce.date().nullable().optional(), // Autogenerated
  status: z.nativeEnum(PromoPrizeRewardStatus).optional(), // Autogenerated
  refKey: z.nativeEnum(PromoPrizeRewardRefKey).nullable().optional(), // Autogenerated
  refId: z.number().nullable().optional(), // Autogenerated
  refIdUuid: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the PrizeOrder table */
export const zodSelectPrizeOrder = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  prizeWinId: z.string().nullable(), // Autogenerated
  orderId: z.string().nullable(), // Autogenerated
  promoPrizeRewardId: z.string().nullable(), // Autogenerated
  prizeId: z.number(), // Autogenerated
  status: z.nativeEnum(PrizeOrderStatus), // Autogenerated
  deliveryStreet: z.string().nullable(), // Autogenerated
  deliveryCity: z.string().nullable(), // Autogenerated
  deliveryPostcode: z.string().nullable(), // Autogenerated
  emailAddress: z.string().nullable(), // Autogenerated
  phoneNumber: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['PrizeOrder','additionalInfoFromPlayer'] if the type is needed */
  additionalInfoFromPlayer: z.unknown(), // Autogenerated
  assigneeId: z.string().nullable(), // Autogenerated
  confirmedAt: z.coerce.date().nullable(), // Autogenerated
  confirmedBy: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the PrizeOrder table */
export const zodInsertPrizeOrder = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  prizeWinId: z.string().nullable().optional(), // Autogenerated
  orderId: z.string().nullable().optional(), // Autogenerated
  promoPrizeRewardId: z.string().nullable().optional(), // Autogenerated
  prizeId: z.number(), // Autogenerated
  status: z.nativeEnum(PrizeOrderStatus).optional(), // Autogenerated
  deliveryStreet: z.string().nullable().optional(), // Autogenerated
  deliveryCity: z.string().nullable().optional(), // Autogenerated
  deliveryPostcode: z.string().nullable().optional(), // Autogenerated
  emailAddress: z.string().nullable().optional(), // Autogenerated
  phoneNumber: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['PrizeOrder','additionalInfoFromPlayer'] if the type is needed */
  additionalInfoFromPlayer: z.unknown().optional(), // Autogenerated
  assigneeId: z.string().nullable().optional(), // Autogenerated
  confirmedAt: z.coerce.date().nullable().optional(), // Autogenerated
  confirmedBy: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the PrizeOrderProduct table */
export const zodSelectPrizeOrderProduct = z.object({
  id: z.string(), // Autogenerated
  prizeOrderId: z.string(), // Autogenerated
  productId: z.number(), // Autogenerated
  quantity: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  awardedAt: z.coerce.date().nullable(), // Autogenerated
  status: z.nativeEnum(PrizeOrderStatus), // Autogenerated
  orderNumber: z.string().nullable(), // Autogenerated
  trackingNumber: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the PrizeOrderProduct table */
export const zodInsertPrizeOrderProduct = z.object({
  id: z.string().optional(), // Autogenerated
  prizeOrderId: z.string(), // Autogenerated
  productId: z.number(), // Autogenerated
  quantity: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  awardedAt: z.coerce.date().nullable().optional(), // Autogenerated
  status: z.nativeEnum(PrizeOrderStatus).optional(), // Autogenerated
  orderNumber: z.string().nullable().optional(), // Autogenerated
  trackingNumber: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the PrizeWin table */
export const zodSelectPrizeWin = z.object({
  id: z.string(), // Autogenerated
  ticketEntryId: z.string(), // Autogenerated
  prizeTierId: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  expiresAt: z.coerce.date().nullable(), // Autogenerated
  chosenAt: z.coerce.date().nullable(), // Autogenerated
  status: z.nativeEnum(PrizeWinStatus), // Autogenerated
  prizeOrderId: z.string().nullable(), // Autogenerated
  notes: z.string().nullable(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['PrizeWin','legacyPrizeChoice'] if the type is needed */
  legacyPrizeChoice: z.unknown().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the PrizeWin table */
export const zodInsertPrizeWin = z.object({
  id: z.string().optional(), // Autogenerated
  ticketEntryId: z.string(), // Autogenerated
  prizeTierId: z.number(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  expiresAt: z.coerce.date().nullable().optional(), // Autogenerated
  chosenAt: z.coerce.date().nullable().optional(), // Autogenerated
  status: z.nativeEnum(PrizeWinStatus).optional(), // Autogenerated
  prizeOrderId: z.string().nullable().optional(), // Autogenerated
  notes: z.string().nullable().optional(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['PrizeWin','legacyPrizeChoice'] if the type is needed */
  legacyPrizeChoice: z.unknown().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the Subscription table */
export const zodSelectSubscription = z.object({
  id: z.string(), // Autogenerated
  playerId: z.string(), // Autogenerated
  orderId: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  status: z.nativeEnum(SubscriptionStatus), // Autogenerated
  startDate: z.coerce.date(), // Autogenerated
  paymentDow: z.number(), // Autogenerated
  nextPaymentDate: z.coerce.date(), // Autogenerated
  recurringPaymentToken: z.string().nullable(), // Autogenerated
  currentRun: z.number().nullable(), // Autogenerated
  failureCount: z.number(), // Autogenerated
  failedAt: z.coerce.date().nullable(), // Autogenerated
  cancelledAt: z.coerce.date().nullable(), // Autogenerated
  snoozedUntil: z.coerce.date().nullable(), // Autogenerated
  version: z.number(), // Autogenerated
});
/** The Zod schema for an INSERT of the Subscription table */
export const zodInsertSubscription = z.object({
  id: z.string().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  orderId: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  status: z.nativeEnum(SubscriptionStatus).optional(), // Autogenerated
  startDate: z.coerce.date(), // Autogenerated
  paymentDow: z.number(), // Autogenerated
  nextPaymentDate: z.coerce.date(), // Autogenerated
  recurringPaymentToken: z.string().nullable().optional(), // Autogenerated
  currentRun: z.number().nullable().optional(), // Autogenerated
  failureCount: z.number().optional(), // Autogenerated
  failedAt: z.coerce.date().nullable().optional(), // Autogenerated
  cancelledAt: z.coerce.date().nullable().optional(), // Autogenerated
  snoozedUntil: z.coerce.date().nullable().optional(), // Autogenerated
  version: z.number().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the SubscriptionHistory table */
export const zodSelectSubscriptionHistory = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  lastModified: z.coerce.date(), // Autogenerated
  subscriptionId: z.string(), // Autogenerated
  action: z.string(), // Autogenerated
  additionalInfo: ZSubscriptionHistoryAdditionalInfo.nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the SubscriptionHistory table */
export const zodInsertSubscriptionHistory = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  lastModified: z.coerce.date().optional(), // Autogenerated
  subscriptionId: z.string(), // Autogenerated
  action: z.string(), // Autogenerated
  additionalInfo: ZSubscriptionHistoryAdditionalInfo.nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the SubscriptionRun table */
export const zodSelectSubscriptionRun = z.object({
  id: z.string(), // Autogenerated
  playerId: z.string().nullable(), // Autogenerated
  subscriptionId: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  status: z.nativeEnum(SubscriptionRunStatus), // Autogenerated
  runNumber: z.number(), // Autogenerated
  drawOrderId: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the SubscriptionRun table */
export const zodInsertSubscriptionRun = z.object({
  id: z.string().optional(), // Autogenerated
  playerId: z.string().nullable().optional(), // Autogenerated
  subscriptionId: z.string(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  status: z.nativeEnum(SubscriptionRunStatus).optional(), // Autogenerated
  runNumber: z.number(), // Autogenerated
  drawOrderId: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the TreeLog table */
export const zodSelectTreeLog = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  playerId: z.string(), // Autogenerated
  status: z.nativeEnum(TreeStatus), // Autogenerated
  action: z.nativeEnum(TreeAction), // Autogenerated
  refId: z.string(), // Autogenerated
  refKey: z.nativeEnum(TreeRefKey), // Autogenerated
  orderId: z.string().nullable(), // Autogenerated
  quantity: z.number(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['TreeLog','additionalInfo'] if the type is needed */
  additionalInfo: z.unknown().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the TreeLog table */
export const zodInsertTreeLog = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  status: z.nativeEnum(TreeStatus), // Autogenerated
  action: z.nativeEnum(TreeAction), // Autogenerated
  refId: z.string(), // Autogenerated
  refKey: z.nativeEnum(TreeRefKey), // Autogenerated
  orderId: z.string().nullable().optional(), // Autogenerated
  quantity: z.number(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['TreeLog','additionalInfo'] if the type is needed */
  additionalInfo: z.unknown().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the Ticket table */
export const zodSelectTicket = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  status: z.nativeEnum(TicketStatus), // Autogenerated
  resultHash: z.string().nullable(), // Autogenerated
  paymentRequestId: z.string(), // Autogenerated
  drawOrderId: z.string(), // Autogenerated
  cost: z.number().nullable(), // Autogenerated
  discount: z.number().nullable(), // Autogenerated
  paid: z.number().nullable(), // Autogenerated
  token: z.number().nullable(), // Autogenerated
  drawId: z.number(), // Autogenerated
  drawTypePlanId: z.number(), // Autogenerated
  playerId: z.string(), // Autogenerated
  cancelledReason: z.string().nullable(), // Autogenerated
  cancelledAt: z.coerce.date().nullable(), // Autogenerated
  cancelledBy: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the Ticket table */
export const zodInsertTicket = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  status: z.nativeEnum(TicketStatus).optional(), // Autogenerated
  resultHash: z.string().nullable().optional(), // Autogenerated
  paymentRequestId: z.string(), // Autogenerated
  drawOrderId: z.string(), // Autogenerated
  cost: z.number().nullable().optional(), // Autogenerated
  discount: z.number().nullable().optional(), // Autogenerated
  paid: z.number().nullable().optional(), // Autogenerated
  token: z.number().nullable().optional(), // Autogenerated
  drawId: z.number(), // Autogenerated
  drawTypePlanId: z.number(), // Autogenerated
  playerId: z.string(), // Autogenerated
  cancelledReason: z.string().nullable().optional(), // Autogenerated
  cancelledAt: z.coerce.date().nullable().optional(), // Autogenerated
  cancelledBy: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the TicketEntry table */
export const zodSelectTicketEntry = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  ticketId: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['TicketEntry','entry'] if the type is needed */
  entry: z.unknown(), // Autogenerated
  resultNumberMatches: z.number().nullable(), // Autogenerated
  resultBonusNumberMatches: z.number().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the TicketEntry table */
export const zodInsertTicketEntry = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  ticketId: z.string(), // Autogenerated
  /* generateZodSchemas.ts didn't know the type to use for this, so uses unknown. Add it to zodUnknownOverrides with path ['TicketEntry','entry'] if the type is needed */
  entry: z.unknown(), // Autogenerated
  resultNumberMatches: z.number().nullable().optional(), // Autogenerated
  resultBonusNumberMatches: z.number().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the TokenOrder table */
export const zodSelectTokenOrder = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  playerId: z.string(), // Autogenerated
  orderId: z.string(), // Autogenerated
  tokenAmount: z.number(), // Autogenerated
  status: z.nativeEnum(TokenOrderStatus), // Autogenerated
});
/** The Zod schema for an INSERT of the TokenOrder table */
export const zodInsertTokenOrder = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  orderId: z.string(), // Autogenerated
  tokenAmount: z.number(), // Autogenerated
  status: z.nativeEnum(TokenOrderStatus).optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the TokenRedemption table */
export const zodSelectTokenRedemption = z.object({
  id: z.string(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  playerId: z.string(), // Autogenerated
  tokenRedemptionOptionId: z.number(), // Autogenerated
  orderId: z.string().nullable(), // Autogenerated
  status: z.nativeEnum(TokenRedemptionStatus), // Autogenerated
  notes: z.string().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the TokenRedemption table */
export const zodInsertTokenRedemption = z.object({
  id: z.string().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  playerId: z.string(), // Autogenerated
  tokenRedemptionOptionId: z.number(), // Autogenerated
  orderId: z.string().nullable().optional(), // Autogenerated
  status: z.nativeEnum(TokenRedemptionStatus).optional(), // Autogenerated
  notes: z.string().nullable().optional(), // Autogenerated
});

/** The Zod schema for a SELECT of the TokenRedemptionOption table */
export const zodSelectTokenRedemptionOption = z.object({
  id: z.number(), // Autogenerated
  createdAt: z.coerce.date(), // Autogenerated
  updatedAt: z.coerce.date(), // Autogenerated
  code: z.string(), // Autogenerated
  slug: z.string(), // Autogenerated
  type: z.nativeEnum(TokenRedemptionOptionType), // Autogenerated
  prizeTierId: z.number().nullable(), // Autogenerated
  drawTypePlanId: z.number().nullable(), // Autogenerated
  price: z.number(), // Autogenerated
  status: z.nativeEnum(TokenRedemptionOptionStatus), // Autogenerated
  title: z.string().nullable(), // Autogenerated
  description: z.string().nullable(), // Autogenerated
  heroImage: z.string().nullable(), // Autogenerated
  group: z.string().nullable(), // Autogenerated
  groupOrder: z.number().nullable(), // Autogenerated
});
/** The Zod schema for an INSERT of the TokenRedemptionOption table */
export const zodInsertTokenRedemptionOption = z.object({
  id: z.number().optional(), // Autogenerated
  createdAt: z.coerce.date().optional(), // Autogenerated
  updatedAt: z.coerce.date().optional(), // Autogenerated
  code: z.string(), // Autogenerated
  slug: z.string(), // Autogenerated
  type: z.nativeEnum(TokenRedemptionOptionType), // Autogenerated
  prizeTierId: z.number().nullable().optional(), // Autogenerated
  drawTypePlanId: z.number().nullable().optional(), // Autogenerated
  price: z.number(), // Autogenerated
  status: z.nativeEnum(TokenRedemptionOptionStatus).optional(), // Autogenerated
  title: z.string().nullable().optional(), // Autogenerated
  description: z.string().nullable().optional(), // Autogenerated
  heroImage: z.string().nullable().optional(), // Autogenerated
  group: z.string().nullable().optional(), // Autogenerated
  groupOrder: z.number().nullable().optional(), // Autogenerated
});
